import { disposalTypeForSubmissionId, hashSubmissionId } from './disposal.ts';

/** Orange rim / pulse before disposal (visual spec 250–400ms). */
export const RAFFLE_SELECT_MS = 320;

/** Short Fan-tap wiggle on the matching Stage ticket. */
export const RAFFLE_WIGGLE_MS = 560;

export const raffleTicketWiggleVariants = {
  rest: { rotate: 0, x: 0 },
  wiggle: {
    rotate: [0, -5, 4, -3, 2, -1, 0],
    x: [0, -4, 3, -2, 2, -1, 0],
    transition: { duration: RAFFLE_WIGGLE_MS / 1000, ease: 'easeInOut' },
  },
};

export function raffleDisposalDurationMs(submissionId: string): number {
  const type = disposalTypeForSubmissionId(submissionId);
  if (type === 'fly-off') return 700;
  if (type === 'flames') return 800;
  return 900;
}

export type RaffleTicketVisual =
  | 'alive'
  | 'selected'
  | 'disposing'
  | 'empty'
  | 'winner'
  | 'loser';

export type RaffleDrawMotion = {
  selectedId: string | null;
  disposingId: string | null;
  emptiedIds: ReadonlySet<string>;
};

export const EMPTY_RAFFLE_DRAW_MOTION: RaffleDrawMotion = {
  selectedId: null,
  disposingId: null,
  emptiedIds: new Set(),
};

export function raffleTicketVisual({
  status,
  submissionId,
  remainingIds,
  winnerIds,
  motion,
}: {
  status?: string | null;
  submissionId: string;
  remainingIds: readonly string[];
  winnerIds: readonly string[];
  motion: RaffleDrawMotion;
}): RaffleTicketVisual {
  if (status === 'COMPLETED') {
    return winnerIds.includes(submissionId) ? 'winner' : 'loser';
  }

  if (status !== 'DRAWING') {
    return 'alive';
  }

  if (motion.emptiedIds.has(submissionId)) {
    return 'empty';
  }
  if (motion.selectedId === submissionId) {
    return 'selected';
  }
  if (motion.disposingId === submissionId) {
    return 'disposing';
  }
  if (remainingIds.includes(submissionId)) {
    return 'alive';
  }
  return 'empty';
}

/** Remaining count updates only after the current disposal finishes. */
export function raffleDisplayedRemainingCount({
  remainingIds,
  lastEliminatedId,
  motion,
}: {
  remainingIds: readonly string[];
  lastEliminatedId?: string | null;
  motion: RaffleDrawMotion;
}): number {
  const base = remainingIds.length;
  if (
    lastEliminatedId &&
    !remainingIds.includes(lastEliminatedId) &&
    !motion.emptiedIds.has(lastEliminatedId)
  ) {
    return base + 1;
  }
  return base;
}

export const DEFAULT_RAFFLE_CLAIM_COPY =
  'Claim your prize at the *front desk* — show your ticket';

export const DEFAULT_RAFFLE_TICKET_FLIP_DIRECTION = 'ADMIT_TO_DRAWING' as const;

export type RaffleTicketFlipDirection = 'ADMIT_TO_DRAWING' | 'DRAWING_TO_ADMIT';

export function raffleTicketFlipDirection(
  config?: { ticketFlipDirection?: string | null } | null,
): RaffleTicketFlipDirection {
  return config?.ticketFlipDirection === 'DRAWING_TO_ADMIT'
    ? 'DRAWING_TO_ADMIT'
    : DEFAULT_RAFFLE_TICKET_FLIP_DIRECTION;
}

/** Admit is the printed front; drawing is the signed back. */
export function raffleTicketStartVisibleSide(
  direction: RaffleTicketFlipDirection,
): 'front' | 'back' {
  return direction === 'DRAWING_TO_ADMIT' ? 'back' : 'front';
}

/**
 * 3D card starts on `visibleSide`. A delayed Stage/form flip inverts once.
 * Fan post-submit has no delayed flip and must follow `visibleSide` so a
 * session that opened on the signed back can still show the printed front.
 */
export function raffleTicketIsFlipped({
  visibleSide,
  delayedFlipElapsed,
}: {
  visibleSide: 'front' | 'back';
  delayedFlipElapsed: boolean;
}): boolean {
  const startOnBack = visibleSide === 'back';
  return delayedFlipElapsed ? !startOnBack : startOnBack;
}

/** Fan after submit: mustard printed face (TICKET / serial / HappiScreen). */
export function raffleTicketFanVisibleSide(): 'front' {
  return 'front';
}

/**
 * Freeze each ticket's flip direction the first time it appears on Stage so a
 * later admin config change does not re-flip tickets already on screen.
 */
export function rememberRaffleTicketFlipDirection(
  seen: Map<string, RaffleTicketFlipDirection>,
  submissionId: string,
  current: RaffleTicketFlipDirection,
): RaffleTicketFlipDirection {
  const existing = seen.get(submissionId);
  if (existing) {
    return existing;
  }
  seen.set(submissionId, current);
  return current;
}

export function raffleClaimCopy(
  config?: { claimYourPrize?: string | null } | null,
): string {
  const fromAdmin = config?.claimYourPrize?.trim();
  return fromAdmin && fromAdmin.length > 0
    ? fromAdmin
    : DEFAULT_RAFFLE_CLAIM_COPY;
}

export function raffleTicketSerial(submissionId: string): string {
  return String(hashSubmissionId(submissionId) % 1_000_000).padStart(6, '0');
}

/** Settled winner tilt in the ±3–6° range. */
export function raffleWinnerTilt(submissionId: string): number {
  const hash = hashSubmissionId(submissionId);
  const magnitude = 3 + (hash % 4);
  return hash % 2 === 0 ? magnitude : -magnitude;
}

export function splitAccentCopy(
  copy: string,
): { text: string; accent: boolean }[] {
  return copy
    .split(/(\*[^*]+\*)/g)
    .filter((part) => part.length > 0)
    .map((part) => {
      if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
        return { text: part.slice(1, -1), accent: true };
      }
      return { text: part, accent: false };
    });
}
